import { Field, useFormikContext } from 'formik';
import React, { FC, useEffect, useState } from 'react';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import deleteIcon from '../../images/Delete.svg';
import { Tag } from '../../models/Tag';
import { getCredentialNameTypesWithPagination } from '../../services/credentialsService';
import { setTime } from '../../services/utilities/dateFormatting';
import { requiredValidation, validateEndDate, validateEndTimeWithDate } from '../../utilities/Validation';
import DateField from '../date-field/date-field';
import Label from '../label/label';
import PaginatedSearchableSelectField from '../selection-field/paginated-searchable-select-field';
import SettingSelectField, { SelectType } from '../settingFields/SettingSelectField';
import TimeField from '../timeField/timeField';
import './enrol-credentials.css';
import { alphabetRegex, alphaNumericRegex, digit } from '../../utilities/regex';
import { FormatOptions, H10302_37_BIT_RAW } from '../../models/FormatOptions';
import { MultiSelectOption } from '../../models/MultiSelectOption';
import { HID_37_NUMBER_OF_BITS } from '../../constants/credential-constants';
import { getTagTypes } from '@/constants/tag-type';

interface Props {
    onBack: (event: any) => void | undefined;
    onNext: (event: any) => void | undefined;
    cardInfo: Tag;
    isEdit?: boolean;
}

const DetailsForm: FC<Props> = ({ onNext, onBack, cardInfo, isEdit = false }) => {
    const HID37BitsCard: number[] = [26, 37] //For now this includes H10304, H10302, and H10301
    const { translate } = useLocale();
    const { errors, values, setFieldValue, isValid, validateForm } = useFormikContext<any>();
    const specialKeys: string[] = ['Backspace', 'Tab'];
    const [enableNext, setEnableNext] = useState<boolean>(false);

    useEffect(() => {
        if (values.id.toString() !== "0" || values.tagDisplay !== '' || values.expiryDate || values.startDate || values.expiryTime || values.startTime) {
            validateForm();
        }

        setEnableNext(values.tagDisplay && values.typeId && isValid);
    }, [values, isValid])

    useEffect(() => {
        if (cardInfo.tagDisplay && cardInfo.typeId) {
            setFieldValue('type', cardInfo.type);
            setFieldValue('typeId', cardInfo.typeId);
            setFieldValue('code', cardInfo.code);
            setFieldValue('tagDisplay', cardInfo.tagDisplay);
            setFieldValue('startDate', cardInfo.startDate !== "" && cardInfo?.startDate ? new Date(cardInfo.startDate) : undefined);
            setFieldValue('expiryDate', cardInfo.expiryDate !== "" && cardInfo.expiryDate ? new Date(cardInfo.expiryDate) : undefined);
            setFieldValue('startTime', cardInfo.startTime !== "--:--" && cardInfo.startTime ? setTime(cardInfo.startTime) : undefined);
            setFieldValue('expiryTime', cardInfo.expiryTime !== "--:--" && cardInfo.expiryTime ? setTime(cardInfo.expiryTime) : undefined);
            setFieldValue('statusId', cardInfo.statusId);
            setFieldValue('maIdentifier', cardInfo.maIdentifier);
            setFieldValue('codeUntruncated', cardInfo.codeUntruncated);
            setFieldValue('truncationFormat', cardInfo.truncationFormat);
            setFieldValue('facilityCode', cardInfo.facilityCode);
            setFieldValue('credentialIdentifier', parseInt(cardInfo.codeUntruncated, 10).toString(16));
            setFieldValue('credentialNumber', cardInfo.credentialNumber);
            setFieldValue('tagByteReversed', cardInfo.tagByteReversed);
        }
    }, [cardInfo]);

    /*
      tagDisplay is the field that is displayed to the user. It is either the tagCodeUntruncated or the tagCode 
      depending on whether we are showing a vehicle registration plate or not. When tagDisplay is edited, we need 
      to update the tag field to match the value of tagDisplay. This is done to ensure the tagCode 
      is updated before saving. The backend will then update tagDisplay accordingly. 
    */
    useEffect(() => {
        setFieldValue('code', values.tagDisplay)
    }, [values.tagDisplay]);

    const blockOrAllowKey = (event) => {
        const regex = values.typeId === '11' ? alphaNumericRegex : digit

        if (specialKeys.includes(event.key)) {

        }
        else if (event.target.value.length >= 20) {
            event.preventDefault();
        }
        else if (event.target.value.length === 0 && event.key === '0') {
            event.preventDefault();
        }
        else if (!event.key.match(regex)) {
            event.preventDefault();
        }
    }

    const onCardTypeSelected = (value) => {
        const isDigitsOnly = values.tagDisplay.match(alphabetRegex);

        if (isDigitsOnly && value !== '11') {
            setFieldValue('tagDisplay', '')
        }

        setFieldValue('typeId', value);
        setFieldValue('type', getCardTypeLabel(value));
    }

    const getCardType = (cardType: string) => {
        // Convert TagTypes into an array of values
        const tagTypesArray = Object.values(getTagTypes());

        // Find the matching type
        const matchedType = tagTypesArray.find((tagType) => String(cardType) === String(tagType.value));

        // Return the matched type or default to Slim
        return matchedType || getTagTypes().Slim;
    }

    const getCardTypeLabel = (cardType: string) => {
        return getCardType(cardType).label;
    }

    const onLocalNext = (event) => {
        if (values.numberOfBits && HID37BitsCard.includes(values.numberOfBits)) {
            setFieldValue('truncationFormatId', values.truncationFormatId);
            setFieldValue('truncationFormat', FormatOptions.find((format: MultiSelectOption) => format.value === values.truncationFormatId)?.label || '');
        }
        onNext({ ...event, tagDisplay: values.tagDisplay });
    }

    const isCardNumberFieldDisabled = () => {
        return HID37BitsCard.includes(cardInfo.numberOfBits ?? 0) && (values.typeId && values.typeId !== "0" && cardInfo.maIdentifier === undefined)
    }

    return <>
        <div className="flex flex-col pr-3 h-full relative">
            <div className="mb-3 text-portal-blue-primary-shade2 text-xl font-semibold">{translate.byKey(TranslationKeys.credential)}</div>
            {!HID37BitsCard.includes(cardInfo.numberOfBits ?? 0) ?
                <div className="flex flex-row">
                    <div className="flex flex-col flex-1">
                        <div className="flex flex-row justify-between">
                            <Label htmlFor="typeId"
                                className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.type)}:</Label>
                            {errors.typeId ? <span className='text-red-600 text-xs'>{errors.typeId}</span> : null}
                        </div>
                        {cardInfo.id === 0 ?
                            <PaginatedSearchableSelectField
                                name={translate.byKey(TranslationKeys.type)}
                                key={"typeId"}
                                accessor='typeId'
                                dataMethod={getCredentialNameTypesWithPagination}
                                searchAccessor={'trnName'}
                                onSelect={(value) => onCardTypeSelected(value)}
                                selectedItemLabel={values.type || ''} />
                            : <Field
                                name="type"
                                type="text"
                                disabled={true}
                                className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 text-sm bg-gray-input cursor-not-allowed" />}
                    </div>
                </div>
                : null}
            <div className="flex flex-row pt-3">
                <div className="flex flex-col flex-1">
                    <div className="flex flex-row justify-between">
                        <Label
                            htmlFor="tagDisplay"
                            className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.tagCode)}:</Label>
                        {errors.tagDisplay ? <span className='text-red-600 text-xs'>{errors.tagDisplay}</span> : null}
                    </div>
                    <Field
                        type="text"
                        name="tagDisplay"
                        disabled={isCardNumberFieldDisabled()} 
                        className={`flex flex-row items-center pr-2 pl-2 p-1 text-sm w-full bg-gray-input ${isCardNumberFieldDisabled() ? 'text-gray-500 text-sm cursor-not-allowed' : ''}`}
                        onKeyDown={(event) => blockOrAllowKey(event)}
                        validate={(value) => requiredValidation(value, translate)} />
                </div>
            </div>
            {cardInfo.tagByteReversed && cardInfo.tagByteReversed === 1 ?
                <div className="flex flex-row pt-3">
                    <div className="flex flex-col flex-1">
                        <div className="flex flex-row justify-between">
                            <Label htmlFor="reversedBytes" className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.mifareBytesReversed)}:</Label>
                        </div>
                        <Field
                            type="text"
                            value={translate.byKey(TranslationKeys.yes)}
                            disabled={true}
                            className={`flex flex-row items-center pr-2 pl-2 p-1 w-full bg-gray-input text-gray-500 text-sm cursor-not-allowed`} />
                    </div>
                </div> : null}
            {HID37BitsCard.includes(cardInfo.numberOfBits ?? 0) ?
                <>
                    {cardInfo.id !== 0 ?
                        <>
                            <div className="flex flex-row pt-3 justify-between">
                                <div className="flex flex-col flex-1 pr-2">
                                    <Label htmlFor="credentialIdentifier"
                                        className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.credentialIdentifier)}:</Label>
                                    <Field
                                        name="credentialIdentifier"
                                        type="text"
                                        disabled={true}
                                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />
                                </div>
                                <div className="flex flex-col flex-1">
                                    <Label required htmlFor="truncationFormat"
                                        className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.truncationFormat)}:</Label>
                                    <Field
                                        name="truncationFormat"
                                        type="text"
                                        disabled={true}
                                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />
                                </div>
                            </div>
                            {/* <div className="flex flex-row pt-3 justify-between">
                                <div className="flex flex-col flex-1  pr-2">
                                    <Label htmlFor="credentialNumber"
                                        className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.credentialNumber)}:</Label>
                                    <Field
                                        name="credentialNumber"
                                        type="text"
                                        disabled={true}
                                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />
                                </div>
                                <div className="flex flex-col flex-1">
                                    <Label htmlFor="facilityCode"
                                        className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.facilityCode)}:</Label>
                                    <Field
                                        name="facilityCode"
                                        type="text"
                                        disabled={true}
                                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />
                                </div>
                            </div> */}
                        </>
                        : cardInfo.numberOfBits === HID_37_NUMBER_OF_BITS ?
                            <div className="flex flex-row pt-3">
                                <div className="flex flex-col flex-1 w-full">
                                    <Label htmlFor="truncationFormatId"
                                        required
                                        className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.truncationFormat)}:</Label>
                                    <SettingSelectField
                                        key={"truncationFormatId"}
                                        accessor={"truncationFormatId"}
                                        options={FormatOptions}
                                        className='flex flex-row w-full'/>
                                </div>
                            </div> : null}
                </> : null}
            <div className="flex flex-row pt-3">
                <div className="flex flex-col flex-1">
                    <Label htmlFor="statusId"
                        className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.status)}:</Label>
                    <SettingSelectField
                        key={"statusId"}
                        accessor={"statusId"}
                        selectType={SelectType.credentialStatuses}
                        className="">
                    </SettingSelectField>
                </div>
            </div>
            <div className="flex flex-row pt-3">
                <div className="flex flex-col flex-1 mr-2">
                    <div className="flex flex-row justify-between">
                        <Label
                            htmlFor="startDate"
                            className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.startDate)}:</Label>
                        {errors.startDate ? <span className='text-red-600 text-xs'>{errors.startDate}</span> : null}
                    </div>
                    <div className="flex flex-row justify-between" id="startDate">
                        <Field
                            type="text"
                            valueName="startDate"
                            name="startDate"
                            component={DateField} />
                        <div className="flex relative w-6 ml-1"
                            onClick={(e) => { setFieldValue("startDate", undefined) }}>
                            <div className="absolute bottom-0 mb-1 right-0 cursor-pointer">
                                <img src={deleteIcon} alt="" />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex flex-col flex-1">
                    <div className="flex flex-row justify-between">
                        <Label
                            htmlFor="startTime"
                            className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.startTime)}:</Label>
                        {errors.startTime ? <span className='text-red-600 text-xs'>{errors.startTime}</span> : null}
                    </div>
                    <div className="flex flex-row justify-between">
                        <Field
                            type="text"
                            name="startTime"
                            valueName="startTime"
                            component={TimeField} />
                        <div className="flex relative w-6 ml-1"
                            onClick={(e) => { setFieldValue("startTime", undefined) }}>
                            <div className="absolute bottom-0 mb-1 right-0 cursor-pointer">
                                <img src={deleteIcon} alt="" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex flex-row pt-3">
                <div className="flex flex-col flex-1 mr-2">
                    <div className="flex flex-row justify-between">
                        <Label
                            htmlFor="expiryDate"
                            className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.expiryDate)}:</Label>
                        {errors.expiryDate ? <span className='text-red-600 text-xs'>{errors.expiryDate}</span> : null}
                    </div>
                    <div className="flex flex-row justify-between" id='expiryDate'>
                        <Field
                            type="text"
                            valueName="expiryDate"
                            name="expiryDate"
                            component={DateField}
                            validate={(value) => validateEndDate(values.expiryDate, values.startDate, translate)} />
                        <div className="flex relative w-6 ml-1"
                            onClick={(e) => { setFieldValue("expiryDate", undefined) }}>
                            <div className="absolute bottom-0 mb-1 right-0 cursor-pointer">
                                <img src={deleteIcon} alt="" />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex flex-col flex-1">
                    <div className="flex flex-row justify-between">
                        <Label
                            htmlFor="expiryTime"
                            className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.expiryTime)}</Label>
                        {errors.expiryTime ? <span className='text-red-600 text-xs'>{errors.expiryTime}</span> : null}
                    </div>
                    <div className="flex flex-row justify-between">
                        <Field
                            type="text"
                            name="expiryTime"
                            valueName="expiryTime"
                            component={TimeField}
                            validate={(value) => validateEndTimeWithDate(values.startTime, value, values.startDate, values.expiryDate, true, translate)} />
                        <div className="flex relative w-6 ml-1"
                            onClick={(e) => { setFieldValue("expiryTime", undefined) }}>
                            <div className="absolute bottom-0 mb-1 right-0 cursor-pointer">
                                <img src={deleteIcon} alt="" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex flex-row justify-end absolute bottom-0 right-0 pr-3 pb-3 ">
                {!isEdit ? <div className="flex flex-row mt-4">
                    <button
                        className={`secondary secondary-inverse py-1 px-5`}
                        type="button"
                        onClick={onBack}>{translate.byKey(TranslationKeys.back)}</button>
                </div> : null}
                <div className="flex flex-row justify-end mt-4 pl-3">
                    <button
                        id="nextButton"
                        data-cy="nextButton"
                        className={`py-1 px-5 secondary  ${!enableNext ? 'secondary-disabled cursor-not-allowed' : 'text-white bg-portal-blue-primary-shade2'}`}
                        type="button"
                        disabled={!enableNext}
                        onClick={onLocalNext}>{translate.byKey(TranslationKeys.next)}</button>
                </div>
            </div>
        </div>
    </>;
}

export default DetailsForm;
